import { Input } from "@chakra-ui/react"
import {
  DragSortHandle,
  List,
  QuillEditor,
  updateOrdering
} from "@pathwright/blocks/src/shared"
import IconButton from "@pathwright/ui/src/components/button/IconButton"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import produce from "immer"
import { useCallback, useEffect, useMemo, useState } from "react"
import { isMobile } from "../../../utils"
import {
  ensureValues,
  generateMatchingItem,
  getMatchingRows,
  getTextFromHTML,
  normalizeMatchingOrdering
} from "../utils"
import {
  MatchingDragHandle,
  MatchingEditItemWrap,
  MatchingEditRowWrap,
  MatchingEditor,
  MatchingEditorHeader
} from "./ui"

const MatchingRow = ({
  option,
  match,
  onChangeMatch,
  onChangeOption,
  onRemove,
  hideTools
}) => {
  return (
    <MatchingEditRowWrap disabled={hideTools} isMobile={isMobile()}>
      {!hideTools ? (
        <DragSortHandle>
          {() => (
            <MatchingDragHandle className="MatchingHandle">
              <Pathicon
                icon="arrow-double-vertical-2"
                style={{ opacity: "0.7", cursor: "pointer" }}
                className="EditButton"
              />
            </MatchingDragHandle>
          )}
        </DragSortHandle>
      ) : (
        <span className="MatchingSpacer"></span>
      )}
      <MatchingEditItemWrap>
        <QuillEditor
          toolbar={TOOLBAR_CONFIG_TYPES.SIMPLE}
          html={option.label}
          placeholder={`Add choice`}
          onChange={onChangeOption}
          autofocus={false}
        />
      </MatchingEditItemWrap>
      <MatchingEditItemWrap>
        <Input
          className="MatchingMatchInput"
          defaultValue={getTextFromHTML(match.label)}
          onChange={(e) => {
            onChangeMatch(e.target.value)
          }}
          placeholder="Add match"
        />
      </MatchingEditItemWrap>
      {!hideTools ? (
        <IconButton
          icon="trash"
          size={10}
          style={{ color: "gray" }}
          className="EditModeIcon MatchingDeleteButton"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
        />
      ) : (
        <span className="MatchingSpacer"></span>
      )}
    </MatchingEditRowWrap>
  )
}

const MatchingQuestionEditor = (props) => {
  const [rows, setRows] = useState([])
  const [options, setOptions] = useState([])
  const [matches, setMatches] = useState([])
  const [hasNormalized, setHasNormalized] = useState(false)

  useEffect(() => {
    const normalized = normalizeMatchingOrdering(
      ensureValues(props.data.options),
      ensureValues(props.data.matches)
    )
    setOptions(normalized.options)
    setMatches(normalized.matches)
    setHasNormalized(true)
  }, [])

  useEffect(() => {
    if (hasNormalized) {
      setupMatchingRows()
    }
  }, [hasNormalized])

  const setupMatchingRows = () => {
    const nextRows = [
      ...getMatchingRows({
        options,
        matches
      })
    ]

    // Add a new row if needed
    const checkForNew = nextRows.filter((row) => row.isNew)
    if (!checkForNew.length) {
      nextRows.push({
        ...handleGenerateNewRow({ order: nextRows.length + 1 }),
        isNew: true
      })
    }

    setRows(nextRows)
  }

  const handleUpdateItem = useCallback(({ itemType, nextHTML, onUpdate }) => {
    const updateRows = ({
      items: rows,
      item: row,
      index,
      itemType,
      nextHTML
    }) => {
      const nextRow = {
        ...row,
        [itemType]: {
          ...row[itemType],
          label: nextHTML || ""
        }
      }
      const nextRows = [...rows]
      nextRows.splice(index, 1, nextRow)

      if (row.isNew) {
        delete nextRow.isNew
      }

      return nextRows
    }

    const updateFunction = ({ items, item, index }) => {
      const updatedRows = updateRows({
        itemType,
        nextHTML,
        items,
        item,
        index
      })
      return { items: updatedRows }
    }
    onUpdate({ updateFunction })
  }, [])

  const renderRow = useCallback(
    ({ item: row, onRemove, onUpdate, isAtMin }) => {
      const updateItem = (itemType) => (nextHTML) => {
        handleUpdateItem({ itemType, nextHTML, onUpdate })
      }

      const hideTools = row.isNew || isAtMin

      return (
        <MatchingRow
          option={row.option}
          match={row.match}
          onChangeOption={updateItem("option")}
          onChangeMatch={updateItem("match")}
          onRemove={onRemove}
          isNew={row.isNew}
          hideTools={hideTools}
        />
      )
    },
    [handleUpdateItem]
  )

  const handleGenerateNewRow = useCallback(
    ({ order }) => {
      return {
        match: { ...generateMatchingItem({ order }) },
        option: { ...generateMatchingItem({ order }) },
        order,
        isNew: true
      }
    },
    [generateMatchingItem]
  )

  const handleChange = useCallback(
    ({ items: rows, changeType }) => {
      let nextRows = [...rows]
      // Add a "new" row if none exists (ie, this update was a new item)
      if (!rows.filter((r) => r.isNew).length) {
        nextRows.push(handleGenerateNewRow({ order: rows.length + 1 }))
      }

      // Ensure "order" attribute is updated on re-order action
      if (changeType === "sort" || changeType === "remove") {
        nextRows = updateOrdering(nextRows)
        nextRows = produce(rows, (draft) => {
          draft.forEach((row) => {
            row.match.order = row.order
            row.option.order = row.order
          })
        })
      }

      setRows(nextRows)

      const notNew = nextRows.filter((row) => !row.isNew)
      // Fix possible missing labels after converting from other question types
      const nextMatches = notNew.map((r) => ({
        label: "",
        ...r.match
      }))
      const nextOptions = notNew.map((r) => r.option)

      setMatches(nextMatches)
      setOptions(nextOptions)
      props.onChange({
        matches: nextMatches,
        options: nextOptions
      })
    },
    [
      props.onChange,
      handleGenerateNewRow,
      updateOrdering,
      setRows,
      setOptions,
      produce
    ]
  )

  const disabledIndices = useMemo(() => [rows.length - 1], [rows])

  return (
    <MatchingEditor
      style={{ width: "100%", marginBottom: "1.5rem" }}
      className="MatchingEditor"
    >
      <MatchingEditorHeader className="MatchingEditor__header">
        <span className="MatchingSpacer"></span>
        <h4>Choices</h4>
        <h4>Matches</h4>
        <span className="MatchingSpacer"></span>
      </MatchingEditorHeader>
      <List
        direction="vertical"
        disabledIndices={disabledIndices}
        items={rows}
        min={2}
        renderItem={renderRow}
        generateItem={handleGenerateNewRow}
        onChange={handleChange}
        id={props.id}
      />
    </MatchingEditor>
  )
}

export default MatchingQuestionEditor
